import type { ProductFeatureGroup } from "@itaas/pep-contracts";
import type {
  ProductAudienceItem,
  ProductDetailHdConfig,
  ProductOutcomeItem,
  ProductTrustChip,
} from "./product-detail-types";

export const SECURITY_HERO_PRODUCT_ASSET =
  "/assets/products/security/hero/itaas-security-product-dashboard.png";
export const SECURITY_HERO_HUMAN_ASSET =
  "/assets/products/security/hero/itaas-security-field-agent.png?v=1";

/** Physical guarding / private security — not cybersecurity. Catalog SoT only. */
export const SECURITY_AUDIENCES: ProductAudienceItem[] = [
  {
    id: "direction",
    icon: "🏛️",
    titleFr: "Direction",
    titleEn: "Leadership",
    bodyFr: "Pilotez contrats clients, sites et affectations agents depuis une vue consolidée.",
    bodyEn: "Steer client contracts, sites and agent assignments from one consolidated view.",
  },
  {
    id: "ops",
    icon: "🛡️",
    titleFr: "Opérations terrain",
    titleEn: "Field operations",
    bodyFr: "Gérez postes, rotations et déclaration d’incidents pour le gardiennage multi-sites.",
    bodyEn: "Run posts, rotations and incident reporting for multi-site guarding.",
  },
  {
    id: "hr",
    icon: "👮",
    titleFr: "Gestion des agents",
    titleEn: "Agent management",
    bodyFr: "Centralisez grades, certifications et documents avec alertes avant expiration.",
    bodyEn: "Centralize ranks, certifications and documents with alerts before expiry.",
  },
  {
    id: "clients",
    icon: "🏢",
    titleFr: "Clients & sites",
    titleEn: "Clients & sites",
    bodyFr: "Suivez contrats de gardiennage, sites industriels, miniers ou résidentiels.",
    bodyEn: "Track guarding contracts across industrial, mining or residential sites.",
  },
  {
    id: "arsp",
    icon: "🤝",
    titleFr: "Sous-traitance",
    titleEn: "Subcontracting",
    bodyFr: "Préparez la sous-traitance sécurité ARSP via l’écosystème SubPortal.",
    bodyEn: "Prepare ARSP security subcontracting via the SubPortal ecosystem.",
  },
];

export const SECURITY_OUTCOMES: ProductOutcomeItem[] = [
  {
    id: "ops",
    icon: "📋",
    titleFr: "Opérations structurées",
    titleEn: "Structured operations",
    bodyFr: "Digitalisez contrats, sites, postes et affectations sans disperser les registres Excel.",
    bodyEn: "Digitize contracts, sites, posts and assignments without scattered Excel registers.",
  },
  {
    id: "compliance",
    icon: "✅",
    titleFr: "Agents plus conformes",
    titleEn: "More compliant agents",
    bodyFr: "Grades, certifications et documents suivis — alertes avant renouvellement.",
    bodyEn: "Ranks, certifications and documents tracked — alerts before renewal.",
  },
  {
    id: "incidents",
    icon: "📣",
    titleFr: "Incidents tracés",
    titleEn: "Traced incidents",
    bodyFr: "Déclarations d’incidents documentées pour mines, résidences et événements.",
    bodyEn: "Documented incident reports for mines, residences and events.",
  },
  {
    id: "suite",
    icon: "🧩",
    titleFr: "Intégré à la Suite",
    titleEn: "Suite-integrated",
    bodyFr: "S’articule avec RH, Inventory (équipements) et AutoID — pas une solution cyber.",
    bodyEn: "Connects with HR, Inventory (equipment) and AutoID — not a cyber solution.",
  },
];

export const SECURITY_CAPABILITY_GROUPS: ProductFeatureGroup[] = [
  {
    titleFr: "Sites & contrats",
    titleEn: "Sites & contracts",
    features: [
      "Gestion multi-clients et multi-sites",
      "Contrats gardiennage et affectations postes",
    ],
  },
  {
    titleFr: "Agents & conformité",
    titleEn: "Agents & compliance",
    features: [
      "Registre agents, grades et certifications",
      "Documents personnel et alertes d’expiration",
    ],
  },
  {
    titleFr: "Incidents & terrain",
    titleEn: "Incidents & field",
    features: [
      "Déclaration d’incidents documentée",
      "Préparation des rondes terrain",
    ],
  },
  {
    titleFr: "Suite ITaaS",
    titleEn: "ITaaS Suite",
    features: [
      "Intégration RH, Inventory et AutoID",
      "Sous-traitance via SubPortal",
    ],
  },
];

const SECURITY_TRUST_BASE: ProductTrustChip[] = [
  {
    id: "physical",
    labelFr: "Sécurité physique & gardiennage",
    labelEn: "Physical security & guarding",
  },
  {
    id: "sites",
    labelFr: "Sites industriels, miniers et résidentiels",
    labelEn: "Industrial, mining and residential sites",
  },
  {
    id: "local",
    labelFr: "Accompagnement ITaaS RDC",
    labelEn: "ITaaS RDC support",
  },
];

export const SECURITY_HD_CONFIG: ProductDetailHdConfig = {
  productImageSrc: SECURITY_HERO_PRODUCT_ASSET,
  productImageAlt: {
    fr: "Tableau de bord ITaaS Security — gardiennage, sites et agents",
    en: "ITaaS Security dashboard — guarding, sites and agents",
  },
  humanImageSrc: SECURITY_HERO_HUMAN_ASSET,
  humanImageAlt: {
    fr: "Agent de sécurité consultant une tablette sur le terrain",
    en: "Security agent reviewing a tablet in the field",
  },
  visualCaption: {
    fr: "Interface produit ITaaS Security — opérations de sécurité privée",
    en: "ITaaS Security product interface — private security operations",
  },
  audiences: SECURITY_AUDIENCES,
  audienceTitle: {
    fr: "Une plateforme pour chaque acteur du gardiennage",
    en: "A platform for every guarding stakeholder",
  },
  outcomes: SECURITY_OUTCOMES,
  outcomesLead: {
    fr: "Des bénéfices concrets pour piloter sites, agents et incidents terrain.",
    en: "Practical benefits for running sites, agents and field incidents.",
  },
  capabilityGroups: SECURITY_CAPABILITY_GROUPS,
  preferConfigCapabilities: true,
  contextTitle: {
    fr: "Pour sociétés de sécurité privée",
    en: "For private security companies",
  },
  contextLead: {
    fr: "Conçu pour le gardiennage et la supervision terrain — pas une solution antivirus, SIEM ou cybersécurité.",
    en: "Built for guarding and field supervision — not antivirus, SIEM or cybersecurity software.",
  },
  contextFallbackPoints: [
    {
      fr: "Contrats, sites, postes et affectations agents",
      en: "Contracts, sites, posts and agent assignments",
    },
    {
      fr: "Registre agents, grades et certifications",
      en: "Agent registry, ranks and certifications",
    },
    {
      fr: "Incidents documentés — rondes avancées en roadmap",
      en: "Documented incidents — advanced patrols on the roadmap",
    },
  ],
  trustBaseChips: SECURITY_TRUST_BASE,
  showAmbassadorLink: false,
  showEditionShowcase: false,
};
